import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock } from 'lucide-react';
import useServiceContextHook from '../../_hooks/service.context.hook';
import { SportsContext } from '../../_context/SportsContext';
import { formatOdds, formatDelta } from './_helpers/oddsFormat';
import { formatSportsMatchTimeKst } from './_helpers/sportsMatchTime';
import { dedupeMergedSelections } from './_helpers/marketDuplicateHelpers';
import { sortSelectionsByDisplayOrder } from './_helpers/marketRowSorting';
import { SportsBetCartContext } from '../../_context/SportsBetCartContext';
import { buildMarketDisplayTitle } from './figma/marketTitleResolver';
import { getSelectionLabel } from './figma/selectionLabelResolver';
import { getLiveHeaderText, getLiveScore } from './_helpers/liveDisplay';
import {
  enrichInplaySelectionMarketIdentity,
  getCustomerMarketKey,
  getInplayLineGroupStableKey,
  getInplaySelectionStableKey,
  getSourceMarketId,
} from '../../_helpers/inplayMarketIdentity';
import BetCart from './BetCart';
import './sports.css';
import TeamLogo from './TeamLogo';

/** Selection sort 순서 — exact base_line 으로 묶인 후에도 카드 순서 일관성 보장.
 *  FigmaInternationalBoard 의 sortSelections 와 같은 패턴.
 */
const _SEL_ORDER = {
  '1': 0, 'X': 1, 'x': 1, '2': 2,
  '1X': 0, 'X2': 1, '12': 2,
  'Over': 0, 'over': 0, 'Under': 1, 'under': 1,
  'Odd': 0, 'odd': 0, 'Even': 1, 'even': 1,
  'Home': 0, 'home': 0, 'Draw': 1, 'draw': 1, 'Away': 2, 'away': 2,
};
const _sortMergedOdds = (odds = []) => {
  const source = Array.isArray(odds) ? odds : [];
  const displayOrdered = sortSelectionsByDisplayOrder(source);
  if (source.some((odd) => odd?.displaySlot != null || odd?.displayOrder != null || odd?.display_slot != null || odd?.display_order != null)) {
    return displayOrdered;
  }
  return [...source].sort((a, b) => {
    const ao = _SEL_ORDER[String(a.name || '').trim()] ?? 99;
    const bo = _SEL_ORDER[String(b.name || '').trim()] ?? 99;
    return ao - bo;
  });
};

const isNotFoundSportsResponse = (response = {}) => {
  if (!response || response.networkError) return false;
  if (response.status !== false) return false;
  const marker = `${response.code || ''} ${response.message || ''} ${response.error || ''}`;
  return /NOT_FOUND|404|찾을 수 없습니다|경기를 찾을 수 없습니다/i.test(marker);
};

/** HC 계열 (Asian / European / Spread) 감지 — 사용자 확정 규칙 적용 대상.
 *  HC-only base_line set 규칙 적용. 그 외 market 은 기존 fallback 유지.
 */
const _isHCMarket = (mkt) => {
  const code = String(mkt.market_code || mkt.market_type?.code || '');
  if (/^FEED_\d+_(3|13|53|61|64|65|66|67|95|250|281|342|407|433|550|935|1083|1248|1558|1693|1694|2286|2732)$/.test(code)) return true;
  const name = String(buildMarketDisplayTitle(mkt) || mkt.market_name || mkt.market_type_name || mkt.market_type?.name || mkt.name || '');
  return /핸디캡|handicap|spread/i.test(name);
};

const getMarketSelections = (market) => Array.isArray(market?.odds || market?.selections)
  ? (market.odds || market.selections)
  : [];
const getLineGroupSelections = (lineGroup) => Array.isArray(lineGroup?.selections || lineGroup?.odds)
  ? (lineGroup.selections || lineGroup.odds)
  : [];
const getLineGroupDisplay = (lineGroup) => (
  lineGroup?.display
  ?? lineGroup?.lineDisplay
  ?? lineGroup?.line_group_display
  ?? lineGroup?.lineValue
  ?? lineGroup?.signedLine
  ?? lineGroup?.signedBaseLine
  ?? ''
).toString().trim();
const enrichMarketSelections = (market, selections = []) => {
  const sourceMarketSuspended = market?.is_suspended ?? false;
  return selections.map((selection) => ({
    ...enrichInplaySelectionMarketIdentity(selection, market),
    _sourceMarketSuspended: sourceMarketSuspended,
    _sourceLine: market?.line ?? market?.display_line ?? market?.base_line ?? market?.main_line ?? '',
    _sourceBaseLine: market?.base_line ?? '',
  }));
};
const getMarketLineGroups = (market) => {
  const v2LineGroups = Array.isArray(market?.lineGroups)
    ? market.lineGroups.map((lineGroup, index) => ({
      ...lineGroup,
      key: getInplayLineGroupStableKey(lineGroup, market) || lineGroup.key || lineGroup.lineId || lineGroup.line_id || `line-${index}`,
      selections: enrichMarketSelections(market, getLineGroupSelections(lineGroup)),
    })).filter((lineGroup) => lineGroup.selections.length > 0)
    : [];
  if (v2LineGroups.length > 0) return v2LineGroups;
  const selections = Array.isArray(market?._mergedOdds)
    ? market._mergedOdds
    : enrichMarketSelections(market, getMarketSelections(market));
  return selections.length > 0
    ? [{
      key: market?._groupKey || market?.id || market?.market_id || 'default-line',
      display: market?._baseLineLabel || market?.display_line || market?.base_line || market?.line || '',
      selections,
    }]
    : [];
};
const withLineGroupMarketContext = (market, lineGroup) => {
  const lineDisplay = getLineGroupDisplay(lineGroup);
  return {
    ...market,
    lineGroups: [lineGroup],
    _lineGroupKey: getInplayLineGroupStableKey(lineGroup, market) || lineGroup?.key || lineGroup?.lineId || null,
    _lineGroupDisplay: lineDisplay || null,
    _baseLineLabel: lineDisplay || market?._baseLineLabel || null,
    display_line: lineDisplay || market?.display_line,
    base_line: lineDisplay || market?.base_line,
    line: lineDisplay || market?.line,
  };
};

const getHandicapDisplayGroupTypeKey = (mkt) => (
  mkt?.market_type_id
  ?? mkt?.market_type?.id
  ?? mkt?.market_type?.code
  ?? mkt?.feed_market_id
  ?? mkt?.market_code
  ?? buildMarketDisplayTitle(mkt)
  ?? mkt?.market_name
  ?? ''
);

/** 사용자 확정 규칙 (2026-04-08): HC 계열만 exact base_line set.
 *  - HC: 같은 (market_type_id, exact base_line) 의 split row 들을 한 section 으로 합침
 *  - 비-HC: 기존대로 row 단위 단독 section (display_line/base_line/line fallback 표시)
 *  - 각 odds 에 source market_id 보존 (cart 의 market_id 정확성)
 */
const _groupMarketsByBaseLine = (markets) => {
  if (!markets) return [];
  const groups = new Map();
  for (const mk of markets) {
    const customerMarketKey = getCustomerMarketKey(mk);
    const sourceMarketId = getSourceMarketId(null, mk) ?? customerMarketKey;
    // initial REST suspend 도 inject — marketLatest 가 비어있는 첫 렌더에 fallback
    const sourceMarketSuspended = mk.is_suspended ?? false;
    const enrichedOdds = getMarketSelections(mk).map(o => ({
      ...enrichInplaySelectionMarketIdentity(o, mk),
      _sourceMarketSuspended: sourceMarketSuspended,
      _sourceLine: mk.line ?? mk.display_line ?? mk.base_line ?? mk.main_line ?? '',
      _sourceBaseLine: mk.base_line ?? '',
    }));
    if (_isHCMarket(mk)) {
      const mtId = getHandicapDisplayGroupTypeKey(mk);
      const baseLine = (mk.base_line ?? '').toString().trim();
      const key = baseLine
        ? `${mtId}::hc::${baseLine}`
        : `${mtId}::hcrow::${customerMarketKey || sourceMarketId || ''}`;
      if (!groups.has(key)) {
        groups.set(key, {
          ...mk,
          _groupKey: key,
          _customerMarketKey: customerMarketKey,
          _sourceMarketId: sourceMarketId,
          _isHCSet: true,
          _baseLineLabel: baseLine || null,
          _mergedOdds: enrichedOdds,
        });
      } else {
        groups.get(key)._mergedOdds.push(...enrichedOdds);
      }
    } else {
      // 비-HC: row 단위, 기존 fallback 유지
      const key = `nohc::${customerMarketKey || sourceMarketId || ''}`;
      groups.set(key, {
        ...mk,
        _groupKey: key,
        _customerMarketKey: customerMarketKey,
        _sourceMarketId: sourceMarketId,
        _isHCSet: false,
        _baseLineLabel: null,
        _mergedOdds: enrichedOdds,
      });
    }
  }
  return Array.from(groups.values());
};

/** 경기 상세 — 전체 마켓/배당 표시 */
const SportsMatch = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { services: { SportsService } } = useServiceContextHook();
  const {
    oddsFlash,
    oddsLatest,
    marketLatest,
    liveMarketProjectionByMatchId,
    registerDetailMatch,
    unregisterDetailMatch,
    displaySettings,
    primeOddsLatest,
    clearInplayMatchMarkets,
  } = useContext(SportsContext);
  const _fmt = displaySettings?.default_odds_format || 'decimal';
  const { toggleSelection, isSelected } = useContext(SportsBetCartContext);

  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockTick, setClockTick] = useState(() => Date.now());

  /** match detail REST 로드 */
  const loadStandaloneDetail = useCallback(async (options = {}) => {
    const silent = options.silent === true;
    if (!silent) setLoading(true);
    const res = await SportsService.getMatchDetail(id);
    if (res?.status && res?.data) {
      setMatch(res.data);
      primeOddsLatest?.(res.data.markets, { overwrite: true, flashChanges: silent });
    } else if (isNotFoundSportsResponse(res)) {
      clearInplayMatchMarkets?.(id, {
        source: 'rest_standalone_detail_not_found',
        hidden: true,
      });
      setMatch(null);
    }
    if (!silent) setLoading(false);
  }, [SportsService, clearInplayMatchMarkets, id, primeOddsLatest]);

  useEffect(() => {
    loadStandaloneDetail();
  }, [loadStandaloneDetail]);

  useEffect(() => {
    if (!match?.is_live || (match?.live_minute === null || match?.live_minute === undefined || match?.live_minute === '')) return undefined;
    const timer = window.setInterval(() => setClockTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [match?.is_live, match?.live_minute]);

  /** socket 구독: 이 match의 실시간 업데이트 수신 */
  useEffect(() => {
    const matchId = Number(id);
    if (!matchId) return;
    registerDetailMatch(matchId);
    return () => unregisterDetailMatch(matchId);
  }, [id, registerDetailMatch, unregisterDetailMatch]);

  useEffect(() => {
    if (!match?.is_live || typeof document === 'undefined') return undefined;
    const interval = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      loadStandaloneDetail({ silent: true });
    }, 4000);
    return () => window.clearInterval(interval);
  }, [match?.is_live, loadStandaloneDetail]);

  const handleOddsClick = (market, odds) => {
    // odds 가 merged section 에서 왔으면 source market_id 우선 (서버 validation 정확성)
    const resolvedMarketId = getSourceMarketId(odds, market) ?? odds.market_id ?? market.source_market_id ?? market.market_id ?? market.id;
    const marketDisplayKey = getCustomerMarketKey(odds, market);
    // suspend pre-check: customer market 기준 (marketLatest 우선, fallback _sourceMarketSuspended)
    const _oddsSus = oddsLatest[odds.id]?.is_suspended ?? odds.is_suspended;
    const _srcMktSus = marketLatest[marketDisplayKey]?.is_suspended ?? odds._sourceMarketSuspended ?? false;
    if (_oddsSus || _srcMktSus) return;
    const selectionNameRaw = odds.name || odds.name_ko || '';
    toggleSelection({
      match_id: match.id,
      market_id: resolvedMarketId,
      source_market_id: resolvedMarketId,
      customer_market_key: marketDisplayKey,
      market_group_key: `${match.id}:${marketDisplayKey}`,
      odds_id: odds.id,
      inplay_odd_id: match.is_live ? odds.id : null,
      match_name: `${match.home_team_ko || match.home_team} vs ${match.away_team_ko || match.away_team}`,
      market_name: buildMarketDisplayTitle(market) || market.market_name || market.name || market.market_code || '마켓',
      market_title_display: buildMarketDisplayTitle(market),
      market_type_name: buildMarketDisplayTitle(market) || market.market_name || market.market_type_name || '',
      // 사용자 확정 규칙 (2026-04-08): HC 계열은 exact base_line, 그 외는 기존 fallback
      market_line: _isHCMarket(market)
        ? (market._baseLineLabel || market.base_line || '')
        : (market.display_line || market.base_line || market.line || market.home_line || ''),
      selection_name: selectionNameRaw,
      selection_name_display: getSelectionLabel(odds, {
        market,
        match,
        fallback: odds.name_ko || selectionNameRaw || '-',
      }),
      home_team: match.home_team,
      away_team: match.away_team,
      home_team_ko: match.home_team_ko,
      away_team_ko: match.away_team_ko,
      odds: oddsLatest[odds.id]?.odds ?? odds.odds,
    });
  };

  // toLocaleString('ko-KR') without a fixed timeZone still reads the
  // browser's LOCAL timezone (the locale only controls language/format, not
  // which timezone is used) — same bug class as the getHours()/getMinutes()
  // formatters elsewhere, fixed by delegating to the shared Asia/Seoul formatter.
  const formatTime = formatSportsMatchTimeKst;

  if (loading) return <div className="sp_page"><div className="sp_loading">로딩중...</div></div>;
  if (!match) return <div className="sp_page"><div className="sp_empty">경기를 찾을 수 없습니다</div></div>;
  const projectionEntry = liveMarketProjectionByMatchId?.[String(match.id)] || null;
  const renderMatch = Array.isArray(projectionEntry?.markets)
    ? { ...match, markets: projectionEntry.markets }
    : match;
  const liveScore = getLiveScore(renderMatch);
  const liveHeader = getLiveHeaderText(renderMatch, clockTick);

  return (
    <div className="sp_page">
      <div className="sp_body">
        <div className="sports_match_detail">
          {/* 뒤로가기 */}
          <button type="button" className="match_detail_back" onClick={() => navigate('/sports')}>
            <ArrowLeft size={16} /> 목록으로
          </button>

          {/* 경기 정보 헤더 */}
          <div className="match_detail_header">
            {renderMatch.is_live && (
              <div className="smd_live_info">
                <span className="sp_live_dot"></span>
                <span className="smd_live_label">LIVE</span>
                <span className="smd_live_score">{liveScore.text}</span>
                {liveHeader && <span className="smd_live_min">{liveHeader}</span>}
              </div>
            )}
            <div className="match_detail_teams">
              <span className="smd_team_with_logo">
                <TeamLogo url={renderMatch.home_team_logo_url} name={renderMatch.home_team_ko || renderMatch.home_team} className="smd_team_logo" />
                {renderMatch.home_team_ko || renderMatch.home_team}
              </span>
              <span className="match_detail_vs">VS</span>
              <span className="smd_team_with_logo">
                {renderMatch.away_team_ko || renderMatch.away_team}
                <TeamLogo url={renderMatch.away_team_logo_url} name={renderMatch.away_team_ko || renderMatch.away_team} className="smd_team_logo" />
              </span>
            </div>
            <div className="match_detail_meta">
              <span style={{display:'flex',alignItems:'center',gap:'4px'}}>
                {renderMatch.league?.icon_url && <img src={renderMatch.league.icon_url} alt="" className="sp_league_logo" />}
                {renderMatch.league?.name_ko || renderMatch.league?.name}
              </span>
              <span className="smd_meta_sep">|</span>
              <span>{formatTime(renderMatch.start_time)}</span>
            </div>
          </div>

          {/* 마켓 카드 목록 — HC 계열만 exact base_line 단위 merge, 그 외는 row-level */}
          <div className="match_markets">
            {_groupMarketsByBaseLine(renderMatch.markets).map(market => {
              const lineGroups = getMarketLineGroups(market).map((lineGroup) => ({
                ...lineGroup,
                selections: _sortMergedOdds(
                  dedupeMergedSelections(getLineGroupSelections(lineGroup), oddsLatest, marketLatest)
                ),
              })).filter((lineGroup) => lineGroup.selections.length > 0);
              const mergedOdds = lineGroups.flatMap((lineGroup) => lineGroup.selections);
              // section-level lock: merged odds 전부가 suspend 일 때만.
              // per-odds 는 source market_id 기준 (marketLatest 우선, fallback _sourceMarketSuspended).
              const mktSus = mergedOdds.length > 0 && mergedOdds.every(o => {
                const oSus = oddsLatest[o.id]?.is_suspended ?? o.is_suspended;
                const srcSus = marketLatest[getCustomerMarketKey(o, market)]?.is_suspended ?? o._sourceMarketSuspended ?? false;
                return oSus || srcSus;
              });
              // HC 는 _baseLineLabel, 비-HC 는 기존 fallback
              const titleMarket = lineGroups.length > 1
                ? {
                  ...market,
                  _baseLineLabel: null,
                  display_line: null,
                  base_line: null,
                  line: null,
                }
                : market;
              const headerLine = titleMarket._isHCSet
                ? (market._baseLineLabel || '')
                : (titleMarket.display_line || titleMarket.base_line || titleMarket.line || '');
              return (
              <div key={market._groupKey || getCustomerMarketKey(market)} className={`market_card ${mktSus ? 'market_suspended' : ''}`}>
                <div className="market_card_header">
                  <span className="market_card_title">
                    {buildMarketDisplayTitle(titleMarket) || titleMarket.market_name || titleMarket.displayName || titleMarket.name || '마켓'}
                    {headerLine && <span className="market_header_line">({String(headerLine).replace(/\s*\(0-0\)\s*$/, '')})</span>}
                  </span>
                  {mktSus && (
                    <span className="market_header_lock"><Lock size={12} /> 베팅 불가</span>
                  )}
                </div>
                <div className="market_card_body">
                  <div className="market_line_groups">
                    {lineGroups.map((lineGroup, lineIndex) => {
                      const lineMarket = withLineGroupMarketContext(market, lineGroup);
                      return (
                        <div key={getInplayLineGroupStableKey(lineGroup, market) || lineGroup.key || `${market._groupKey || getCustomerMarketKey(market)}::${lineIndex}`} className="market_line_group">
                          <div className={`market_odds_grid ${lineGroup.selections.length <= 2 ? "sp_grid_2" : ""}`}>
                            {lineGroup.selections.map(odds => {
                              const selectionStableKey = getInplaySelectionStableKey(odds, lineMarket);
                              const flash = oddsFlash?.[odds.id] || (selectionStableKey ? oddsFlash?.[selectionStableKey] : null);
                              const oddsSelfSus = oddsLatest[odds.id]?.is_suspended ?? odds.is_suspended;
                              // source market 기준 suspend (marketLatest 우선, fallback _sourceMarketSuspended)
                              const srcMktSus = marketLatest[getCustomerMarketKey(odds, lineMarket)]?.is_suspended ?? odds._sourceMarketSuspended ?? false;
                              const oddsSus = oddsSelfSus || srcMktSus;
                              return (
                                <button key={selectionStableKey || odds.id} type="button"
                                  className={`sp_odds_btn smd_odds_btn ${isSelected(odds.id) ? 'sp_selected' : ''} ${oddsSus ? 'sp_suspended' : ''} ${flash ? 'sp_flash_' + flash.direction : ''}`}
                                  onClick={() => handleOddsClick(lineMarket, odds)}
                                  disabled={oddsSus}>
                                  <span className="smd_odds_name">{getSelectionLabel(odds, { market: lineMarket, match: renderMatch })}</span>
                                  <span className="sp_odds_value">
                                    {formatOdds(oddsLatest[odds.id]?.odds ?? odds.odds, _fmt)}
                                  </span>
                                  {flash && <span className={`sp_delta sp_delta_${flash.direction}`}>{flash.direction === 'up' ? '+' : '-'}{formatDelta(flash.delta, _fmt)}</span>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
            })}
            {(renderMatch.markets || []).length === 0 && (
              <div className="sp_empty">등록된 마켓이 없습니다</div>
            )}
          </div>
        </div>
        <BetCart />
      </div>
    </div>
  );
};

export default SportsMatch;
